import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Accounting from "@/pages/Accounting";
import Exports from "@/pages/Exports";
import Catalog from "@/pages/Catalog";
import CreateRelease from "@/pages/CreateRelease";
import CoverRequest from "@/pages/CoverRequest";
import ReleaseDetail from "@/pages/ReleaseDetail";
import SplitSheets from "@/pages/SplitSheets";
import SplitSheetDetail from "@/pages/SplitSheetDetail";
import Predictive from "@/pages/Predictive";
import ManagerSona from "@/pages/ManagerSona";
import SmartLinkLanding from "@/pages/SmartLinkLanding";
import Epk from "@/pages/Epk";
import EpkPublic from "@/pages/EpkPublic";
import AdminPresaves from "@/pages/AdminPresaves";
import AdminOverview from "@/pages/AdminOverview";
import AdminReleases from "@/pages/AdminReleases";
import AdminWithdrawals from "@/pages/AdminWithdrawals";
import AdminSupport from "@/pages/AdminSupport";
import AdminPitching from "@/pages/AdminPitching";
import AdminSmartlinks from "@/pages/AdminSmartlinks";
import AdminStaff from "@/pages/AdminStaff";
import AdminArtists from "@/pages/AdminArtists";
import AdminFreshFinds from "@/pages/AdminFreshFinds";
import FreshFinds from "@/pages/FreshFinds";
import ArtistProfile from "@/pages/ArtistProfile";
import Marketing from "@/pages/Marketing";
import AdminCoverRequests from "@/pages/AdminCoverRequests";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminAccessRequests from "@/pages/AdminAccessRequests";
import AdminAccounts from "@/pages/AdminAccounts";
import LegalPage from "@/pages/LegalPage";
import SignInvite from "@/pages/SignInvite";
import AdminManagers from "@/pages/AdminManagers";
import ArtistLinkGate from "@/components/ArtistLinkGate";
import ManagerPanel from "@/pages/ManagerPanel";
import AdminPanorama from "@/pages/AdminPanorama";
import AdminFirmados from "@/pages/AdminFirmados";
import AdminFinanzas from "@/pages/AdminFinanzas";
import AppStart from "@/components/AppStart";
import SplitAgreement from "@/pages/SplitAgreement";
import GuestSign from "@/pages/GuestSign";
import PresaveLanding from "@/pages/PresaveLanding";
import AuthCallback from "@/pages/AuthCallback";
import Verificar from "@/pages/Verificar";
import HojaDeRuta from "@/pages/HojaDeRuta";
import HojaDeRutaDetalle from "@/pages/HojaDeRutaDetalle";
import HojaPublica from "@/pages/HojaPublica";
import AdminHojaDeRuta from "@/pages/AdminHojaDeRuta";
import OpsRoot from "@/ops/OpsRoot";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-black" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><ErrorBoundary resetKey={window.location.pathname}><ArtistLinkGate>{children}</ArtistLinkGate></ErrorBoundary></Layout>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verificar" element={<Verificar />} />
      <Route path="/presave/:id" element={<PresaveLanding />} />
      <Route path="/l/:slug" element={<SmartLinkLanding />} />
      <Route path="/epk/:slug" element={<EpkPublic />} />
      <Route path="/hoja/:token" element={<HojaPublica />} />
      <Route path="/hoja-de-ruta" element={<Protected><HojaDeRuta /></Protected>} />
      <Route path="/hoja-de-ruta/:id" element={<Protected><HojaDeRutaDetalle /></Protected>} />
      <Route path="/admin/hoja-de-ruta" element={<Protected><AdminHojaDeRuta /></Protected>} />
      <Route path="/predictive" element={<Protected><Predictive /></Protected>} />
      <Route path="/manager" element={<Protected><ManagerSona /></Protected>} />
      <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/accounting" element={<Protected><Accounting /></Protected>} />
      <Route path="/exports" element={<Protected><Exports /></Protected>} />
      <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
      <Route path="/distribute" element={<Protected><CreateRelease /></Protected>} />
      <Route path="/catalog/:id" element={<Protected><ReleaseDetail /></Protected>} />
      <Route path="/covers" element={<Protected><CoverRequest /></Protected>} />
      <Route path="/split-sheets" element={<Protected><SplitSheets /></Protected>} />
      <Route path="/split-sheets/:id" element={<Protected><SplitSheetDetail /></Protected>} />
      <Route path="/epk" element={<Protected><Epk /></Protected>} />
      <Route path="/admin" element={<Protected><AdminOverview /></Protected>} />
      <Route path="/admin/releases" element={<Protected><AdminReleases /></Protected>} />
      <Route path="/admin/withdrawals" element={<Protected><AdminWithdrawals /></Protected>} />
      <Route path="/admin/support" element={<Protected><AdminSupport /></Protected>} />
      <Route path="/admin/pitching" element={<Protected><AdminPitching /></Protected>} />
      <Route path="/admin/smartlinks" element={<Protected><AdminSmartlinks /></Protected>} />
      <Route path="/admin/staff" element={<Protected><AdminStaff /></Protected>} />
      <Route path="/admin/artists" element={<Protected><AdminArtists /></Protected>} />
      <Route path="/admin/fresh-finds" element={<Protected><AdminFreshFinds /></Protected>} />
      <Route path="/fresh-finds" element={<FreshFinds />} />
      <Route path="/perfil-artista" element={<Protected><ArtistProfile /></Protected>} />
      <Route path="/admin/cover-requests" element={<Protected><AdminCoverRequests /></Protected>} />
      <Route path="/admin/access-requests" element={<Protected><AdminAccessRequests /></Protected>} />
      <Route path="/admin/accounts" element={<Protected><AdminAccounts /></Protected>} />
      <Route path="/admin/managers" element={<Protected><AdminManagers /></Protected>} />
      <Route path="/admin/panorama" element={<Protected><AdminPanorama /></Protected>} />
      <Route path="/admin/firmados" element={<Protected><AdminFirmados /></Protected>} />
      <Route path="/admin/finanzas" element={<Protected><AdminFinanzas /></Protected>} />
      <Route path="/mi-sello" element={<Protected><ManagerPanel /></Protected>} />
      <Route path="/mi-sello/finanzas" element={<Protected><ManagerPanel initialView="finanzas" /></Protected>} />
      <Route path="/inicio" element={<Protected><AppStart /></Protected>} />
      <Route path="/acuerdo/:token" element={<SplitAgreement />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/firmar-split/:token" element={<SignInvite />} />
      <Route path="/guest/:token" element={<GuestSign />} />
      <Route path="/admin/marketing" element={<Protected><AdminMarketing /></Protected>} />
      <Route path="/admin/presaves" element={<Protected><AdminPresaves /></Protected>} />
      <Route path="/ops/*" element={<OpsRoot />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
